import React, { useEffect } from 'react';
import styled from 'styled-components';
// Assuming standard imports from your UI library
import StyledButton from "../components/ui/StyledButton";
import {
    Section,
    SectionTitle,
    SubHeadline,
    Highlight,
    HeroSection,
    PageTitle, FigureCaption, FigureWrapper,
} from '../components/ui/PageElements';
import InteractiveSpinePlayer from '../components/InteractiveSpinePlayer';
import ResponsiveImage from '../components/ui/ResponsiveImage';
import ResponsiveVideo from '../components/ui/ResponsiveVideo';
import PageMeta from '../components/PageMeta';

const ProjectWrapper = styled.div`
    max-width: 1200px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    position: relative;
`;

// Maybe a specific style for emphasis on the art
const ArtEmphasis = styled.span`
    font-style: italic;
    color: var(--secondary-color); // Use a secondary color for distinction?
`;

const DevelopmentFocus = styled.div`
    background-color: rgba(0,0,0, 0.5);
    border: 1px solid var(--grid-color);
    padding: 20px 30px;
    margin: 30px auto;
    max-width: 800px;
    text-align: center;
    font-size: 14px;
    color: #cccccc;

    strong {
        color: var(--primary-color);
        text-transform: uppercase;
        letter-spacing: 1px;
        display: block;
        margin-bottom: 10px;
        font-size: 16px;
    }
`;

const ACatGamePage = () => {
    // Apply custom cat paw cursor to entire page (including areas outside content)
    useEffect(() => {
        const styleId = 'acatgame-cursor-styles';
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            body {
                cursor: url("/assets/ACatGame/cursors/DefaultPawCursorXS.png"), auto !important;
            }
            body a, body button, body [role="button"], body input[type="submit"],
            body input[type="button"], body input[type="reset"], body input[type="checkbox"],
            body input[type="radio"], body label[for], body select, body summary, body .clickable {
                cursor: url("/assets/ACatGame/cursors/ExtendedPawCursorXS.png"), pointer !important;
            }
            body a:active, body button:active, body [role="button"]:active,
            body input[type="submit"]:active, body input[type="button"]:active,
            body input[type="reset"]:active {
                cursor: url("/assets/ACatGame/cursors/ClosedPawCursorXS.png"), pointer !important;
            }
            body button:disabled, body input:disabled, body textarea:disabled,
            body select:disabled, body .disabled {
                cursor: url("/assets/ACatGame/cursors/DefaultPawCursorXS.png"), not-allowed !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            const existingStyle = document.getElementById(styleId);
            if (existingStyle) {
                existingStyle.remove();
            }
        };
    }, []);

    // Link to contact page as development follow is not yet available
    const contactLink = "/contact";
    // Define the multi-track animation setup for Dr. Pouet Pouet
    const drPouetPouetAnimations = [
        { track: 0, name: "Laughing", loop: true },          // Main laugh on track 0
        { track: 1, name: "AddLittleCatIdleHumHum", loop: true } // Pet cat idle on track 1
    ];

    return (
        <>
            <PageMeta
                title="A Cat Game"
                description="A hand-painted 2D platformer where art comes alive. Control three cats with unique abilities through a breathing, animated world. In development."
                canonicalPath="/projects/a-cat-game"
            />
            <ProjectWrapper>
                {/* --- Hero Section --- */}
                <HeroSection style={{ paddingBottom: '20px' }}>
                    <PageTitle>A Cat Game <Highlight>// In Development //</Highlight></PageTitle>
                    <SubHeadline>
                        Step into a world painted into existence. A 2D platformer where every frame is a canvas, brought to life with unique animation and handcrafted care.
                    </SubHeadline>
                    <InteractiveSpinePlayer
                        assetPath="/assets/ACatGame/Dacha" // Path relative to /public
                        jsonFile="Dacha.json"
                        atlasFile="Dacha.atlas.txt"
                        initialAnimation="SuperMega" // Or "run", "jump", etc.
                        initialSkin="default" // Or your main character skin name
                        poster="/assets/ACatGame/Dacha/dacha-poster.webp"
                        showControls={false}
                        style={{ maxWidth: '500px', margin: '30px auto 0 auto' }} // Example sizing
                    />
                    {/* Optionally add links to specific devlogs if available */}
                </HeroSection>

                {/* --- The Vision Section --- */}
                <Section style={{ paddingTop: '20px' }}>
                    <SectionTitle>Where Art Breathes</SectionTitle>
                    <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                        What if a game didn't just <ArtEmphasis>look</ArtEmphasis> painted, but <ArtEmphasis>felt</ArtEmphasis> painted? Inspired by real artwork, "A Cat Game" utilizes <Highlight>hand-painted assets</Highlight> animated with 2D skeletal systems. From the characters to the foliage, the world <Highlight>sways, reacts, and breathes</Highlight> in response to physics, wind, and interaction.
                        <br/><br/>
                        Our goal: A playable painting, imbued with a sense of calm, mystery, and a touch of the wonderfully strange.
                    </SubHeadline>
                    <FigureWrapper>
                        <ResponsiveVideo
                            src="/assets/ACatGame/videos/midsomWeb.mp4"
                            alt="A curious cat meets a mysterious, Midsommar-esque feline who commands the flora, culminating in a vibrant burst of petals"
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls={false}
                            clickToPause={false}
                            linkToFullRes={true}
                        />
                        <FigureCaption>
                            A chance encounter in the woods: Watch a curious cat meet a mysterious, Midsommar-esque feline who commands the flora, culminating in a vibrant burst of petals.
                        </FigureCaption>
                    </FigureWrapper>

                </Section>

                {/* --- Gameplay Tease Section --- */}
                <Section>
                    <SectionTitle>Meet the Trio // Fluid Motion</SectionTitle>
                    <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                        Control not one, but <Highlight>three distinct feline protagonists</Highlight> – Svetlana, Katya, and Bretzel – each inspired by a real-life companion. Master their unique movement styles and special abilities: a <Highlight>double jump</Highlight>, a swift <Highlight>gallop</Highlight>, and a <Highlight>blink dash</Highlight>.
                        <br/><br/>
                        Seamlessly switch between cats to chain abilities, uncovering high-velocity maneuvers and rewarding <Highlight>frame-perfect actions</Highlight>. Easy to pick up, but offering emergent depth for those who seek mastery.
                    </SubHeadline>
                    <FigureWrapper>
                        <ResponsiveVideo
                            src="/assets/ACatGame/videos/web.mp4"
                            alt="Svetlana, Katya, and Bretzel showcase their unique abilities: Double Jump, Gallop, and Dash, with fluid ability chaining and slow-motion impacts"
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls={false}
                            clickToPause={false}
                            linkToFullRes={true}
                        />
                        <FigureCaption>
                            Meet the trio: Svetlana, Katya, and Bretzel showcase their unique abilities (Double Jump, Gallop, Dash). Witness fluid ability chaining, power-ups, and satisfying slow-motion impacts in action.
                        </FigureCaption>
                    </FigureWrapper>
                </Section>

                {/* --- World Tease Section --- */}
                <Section>
                    <SectionTitle>A Living Canvas</SectionTitle>
                    <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                        Journey through diverse, hand-painted environments. Explore lush forests reacting to dynamic <Highlight>day/night cycles and weather systems</Highlight>, or discover the unexpected serenity of the <Highlight>moon (complete with cat-sized spacesuits!)</Highlight>.
                        <br/><br/>
                        Experience meticulously crafted lighting, custom shaders enhancing the painted aesthetic, reactive water, and <Highlight>bioluminescent flora</Highlight> that illuminates the mysterious world. Every scene is tuned for atmosphere, complemented by an adaptive soundscape powered by FMOD.
                    </SubHeadline>
                    <FigureWrapper>
                        <ResponsiveImage
                            src="/assets/ACatGame/img/env.gif"
                            alt="Animated GIF cycling through environments in 'A Cat Game': daytime forest, alien bioluminescent plants, stormy forest at night, and an interior moon base."
                            sizes="(max-width: 600px) 90vw, 675px"
                        />
                        <FigureCaption>
                            From sunlit forests and glowing alien landscapes to stormy nights and lunar outposts – explore the diverse, hand-painted worlds of 'A Cat Game'.
                        </FigureCaption>
                    </FigureWrapper>
                </Section>

                {/* --- Story Hook Section --- */}
                <Section>
                    <SectionTitle>A Whisker of Story</SectionTitle>
                    <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                        You begin as Svetlana, an accomplished fisher-cat enjoying a night at sea... until a mysterious storm and an eerie green beam change everything. Awakening on shore, no longer quite herself, she embarks on a journey tangled with the schemes of the nefarious <Highlight>Dr. Pouet Pouet</Highlight> and his canine cohorts.
                        <br/><br/>
                        A quest to save cat-kind unfolds... more details clawing their way soon.
                    </SubHeadline>
                    <FigureWrapper>
                        <InteractiveSpinePlayer
                            assetPath="/assets/ACatGame/Pouet"
                            jsonFile="pouet.json"
                            atlasFile="pouet.atlas.txt"
                            initialSkin="wLitlleCat"
                            initialTrackAnimations={drPouetPouetAnimations}
                            poster="/assets/ACatGame/Pouet/pouet-poster.webp"
                            showControls={false}
                            style={{
                                maxWidth: '400px', // Adjust size
                                margin: '0 auto 10px auto' // Center, add bottom margin
                            }}
                        />
                        <FigureCaption>
                            Beware the signature evil laugh of Dr. Pouet Pouet... and his surprisingly calm pet cat.
                        </FigureCaption>
                    </FigureWrapper>
                </Section>

                {/* --- Development Focus Section --- */}
                <Section>
                    <SectionTitle>The Journey Ahead</SectionTitle>
                    <SubHeadline>
                        "A Cat Game" is actively and organically growing. Development spans across polishing assets, refining core mechanics, and building intricate systems.
                    </SubHeadline>
                    <DevelopmentFocus>
                        <strong>Current Focus Includes:</strong>
                        Building sophisticated AI for bosses and enemies, and implementing a unique, non-grid-based navigation graph for complex and natural movement within our painted world.
                    </DevelopmentFocus>
                    <SubHeadline>
                        We're pouring our hearts into crafting something special. Follow along on the website for updates!
                    </SubHeadline>
                    <FigureWrapper>
                        <ResponsiveImage
                            src="/assets/ACatGame/img/behindTheScene.gif" // Your GIF path
                            alt="Behind the scenes development GIF for A Cat Game, showing glimpses of the Spine animation workflow, a character's behavior tree diagram, a navigation graph being built in a test scene, and a complex water shader graph."
                            sizes="(max-width: 1200px) 100vw, 1200px"
                        />
                        <FigureCaption>
                            From animation workflows to AI logic, nav systems, and shader magic – a quick look at the handcrafted development powering 'A Cat Game'. And we even write actual code sometimes!
                        </FigureCaption>
                    </FigureWrapper>
                </Section>
                {/* --- Final CTA Section --- */}
                <Section style={{ textAlign: 'center' }}>
                    <SectionTitle>Stay Tuned...</SectionTitle>
                    <SubHeadline>
                        The canvas is still wet, the adventure awaits. If you're interested in the project, feel free to reach out.
                    </SubHeadline>
                    <StyledButton to={contactLink}>Contact Me</StyledButton>
                    {/* Add other relevant links if needed - e.g., Discord, Twitter */}
                    {/* <StyledButton href="YOUR_DISCORD_LINK" target="_blank" rel="noopener noreferrer">Join Discord</StyledButton> */}
                    <FigureWrapper style={{ marginTop: '0' }}>
                    <InteractiveSpinePlayer
                        assetPath="/assets/ACatGame/Svet" // Path relative to /public
                        jsonFile="fisherSvet.json"
                        atlasFile="fisherSvet.atlas.txt"
                        initialAnimation="Waving" // Or "run", "jump", etc.
                        initialSkin="default" // Or your main character skin name
                        poster="/assets/ACatGame/Svet/svet-poster.webp"
                        showControls={false}
                        style={{ maxWidth: '500px', margin: '30px auto 0 auto' }} // Example sizing
                    />
                    <FigureCaption>
                        Waving a final paw before the next fishing trip... or nap.
                    </FigureCaption>
                    </FigureWrapper>
                </Section>

            </ProjectWrapper>
        </>
    );
};

export default ACatGamePage;